import { ButtonV3, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { FocusEvent, ReactNode } from 'react';

import Motion from '@/app/components/Motion';

import type {
  HomeHeroContentButtonProps,
  HomeHeroContentCopyProps,
} from './types';

export interface HomeHeroContentProps {
  readonly button: HomeHeroContentButtonProps;
  readonly copy?: HomeHeroContentCopyProps | undefined;
  readonly icon?: ReactNode | undefined;
}

const HomeHeroContent = ({
  button,
  copy,
  icon,
}: HomeHeroContentProps): JSX.Element => {
  const t = useTranslations();

  const handleButtonFocus = (event: FocusEvent<HTMLButtonElement, Element>) => {
    event.preventDefault();
    event.stopPropagation();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    button.onFocus?.(event);
  };

  return (
    <Motion
      animate={{ opacity: 1 }}
      data-cy="home-hero-content"
      initial={{ opacity: 0 }}
      transition={{ duration: 1, type: 'tween' }}
    >
      {copy !== undefined && (
        <TypographyV3
          align="centerMdLeft"
          className="mb-6"
          color={copy.variant ?? 'light'}
          data-cy="home-hero-content-copy"
          Element="p"
          variant="body-large"
        >
          {copy.value}
        </TypographyV3>
      )}
      <div className="mb-8 w-full md:inline-block md:w-fit lg:mb-12">
        <ButtonV3
          {...button}
          className={cx('w-full', button.className)}
          data-cy="home-hero-content-button"
          variant={button.variant ?? 'primary'}
          onFocus={handleButtonFocus}
        >
          {t(button.label)}
        </ButtonV3>
        {icon}
      </div>
    </Motion>
  );
};

export default HomeHeroContent;
